import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

import { isEqual } from "lodash";
import moment from "moment";

import { useCustomGoalsContext } from "@components/data/custom-goals-provider";
import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import { useCustomMetricsContext } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { generateDateLabels } from "@features/sales-v3/utils/chart";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { useUniversalBenchmarksLoader } from "@features/universal-benchmarks";
import { METRICS_EXPLORER_REPORT } from "@gql/metrics-explorer";
import { NORTHBEAM_INDEX } from "@gql/northbeam-index";
import { GET_TOP_SPEND_PLATFORMS } from "@gql/top-spend-platforms";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import {
  type GetNorthbeamIndex,
  type GetNorthbeamIndexVariables,
  type GetTopSpendPlatforms,
} from "@north-beam/nb-client-side";
import { getMetrics } from "@north-beam/nb-client-side/metrics-v2";
import {
  type GetMetricsExplorerReport,
  type GetMetricsExplorerReportVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import {
  todayLocalAsISODate,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { type SalesMetricRow } from "@north-beam/nb-common/metrics-v2";
import { parseDateRangeArgument } from "@utils/index";

import {
  type BenchmarkData,
  type MetricData,
  type MetricsExplorerChartState,
  type MetricsExplorerContextDescriptor,
  type MetricsExplorerFilters,
  type NorthbeamIndexData,
  type TileDescriptor,
} from "./types";
import { buildAdvancedSearchJson, deriveLevelFromSearch } from "./utils";

export const MetricsExplorerContext =
  createContext<MetricsExplorerContextDescriptor | null>(null);

interface Props {
  filters: Omit<MetricsExplorerFilters, "dateRangeInterval">;
  chartState: MetricsExplorerChartState;
  tiles: TileDescriptor[];
  setTiles(value: TileDescriptor[]): void;
  setAlignAxesScale(value: boolean): void;
}

export const MetricsExplorerProvider = ({
  filters,
  chartState,
  tiles,
  setTiles,
  setAlignAxesScale,
  children,
}: PropsWithChildren<Props>) => {
  const { user } = useUser();
  const { addQueryMetadata } = useQueryMetadata();

  const loadAdoptedBenchmarks = useUniversalBenchmarksLoader();

  const [pdfExportAreaRef, setPdfExportAreaRef] = useState<HTMLElement | null>(
    null,
  );
  const [metricsData, setMetricsData] = useState<MetricData[]>([]);
  const [benchmarksData, setBenchmarksData] = useState<BenchmarkData[]>([]);
  const [indexData, setIndexData] = useState<NorthbeamIndexData[]>([]);

  const dateRangeInterval = useMemo(
    () => parseDateRangeArgument(filters.dateRange, todayLocalAsISODate()),
    [filters.dateRange],
  );

  const { customGoals } = useCustomGoalsContext();
  const { customMetrics } = useCustomMetricsContext();

  const { hasFeatureFlag } = useFeatureFlags();

  const buildMetrics = useCallback(
    ({
      attributionModel,
      attributionWindow,
      accountingMode,
    }: {
      attributionModel: string;
      attributionWindow: IMCAttributionWindowDays;
      accountingMode: INBAccountingMode;
    }) => {
      return getMetrics({
        user,
        attributionModel,
        attributionWindow,
        accountingMode,
        timeGranularity: filters.timeGranularity,
        showV1Forecasts: hasFeatureFlag("showV1Forecasts"),
        customGoalRows: customGoals,
        customMetricRows: customMetrics,
        includeAllCustomMetrics: true,
        isDashboardV2: hasFeatureFlag("enableDashboardV2"),
      });
    },
    [filters.timeGranularity, customGoals, customMetrics, user, hasFeatureFlag],
  );

  const loadMetricData = useCallback(
    (variables: GetMetricsExplorerReportVariables) => {
      return user.apolloClient.query<
        GetMetricsExplorerReport,
        GetMetricsExplorerReportVariables
      >({
        query: METRICS_EXPLORER_REPORT,
        variables,
      });
    },
    [user.apolloClient],
  );

  const loadNorthbeamIndexData = useCallback(
    (variables: GetNorthbeamIndexVariables) => {
      return user.apolloClient.query<
        GetNorthbeamIndex,
        GetNorthbeamIndexVariables
      >({
        query: NORTHBEAM_INDEX,
        variables,
      });
    },
    [user.apolloClient],
  );

  const prevTiles = useRef<TileDescriptor[]>([]);

  useEffect(() => {
    const getUpdatedKeys = (tile: TileDescriptor) => {
      const keys = Object.keys(tile);
      const prevTile = prevTiles.current.find((e) => e.id === tile.id);

      if (!prevTile) {
        return keys;
      }

      return keys.filter(
        (key) =>
          !isEqual(
            tile[key as keyof TileDescriptor],
            prevTile[key as keyof TileDescriptor],
          ),
      );
    };

    const anyTileUpdated = tiles.some(
      (tile) => getUpdatedKeys(tile).length > 0,
    );

    const onlyActiveKeyUpdated = tiles.every((tile) => {
      const keys = getUpdatedKeys(tile);
      return keys.length === 0 || isEqual(keys, ["active"]);
    });

    const onlyVisibleKeyUpdated = tiles.every((tile) => {
      const keys = getUpdatedKeys(tile);
      return keys.length === 0 || isEqual(keys, ["visible"]);
    });

    const onlyNameKeyUpdated = tiles.every((tile) => {
      const keys = getUpdatedKeys(tile);
      return keys.length === 0 || isEqual(keys, ["name"]);
    });

    if (
      anyTileUpdated &&
      (onlyActiveKeyUpdated || onlyVisibleKeyUpdated || onlyNameKeyUpdated)
    ) {
      prevTiles.current = tiles;
      return;
    }

    setMetricsData(
      tiles
        .filter((tile) => {
          const metrics = buildMetrics({
            attributionModel: tile.attributionModel ?? filters.attributionModel,
            attributionWindow:
              tile.attributionWindow ?? filters.attributionWindow,
            accountingMode: tile.accountingMode ?? filters.accountingMode,
          });

          const metric = metrics.find((e) => e.id === tile.metricId);

          if (!metric) {
            return false;
          }

          if (!hasFeatureFlag("enableDashboardV2") && metric.isDashboardV2) {
            return false;
          }

          return true;
        })
        .map((tile) => ({
          tileId: tile.id,
          status: "loading",
          data: null,
        })),
    );

    tiles.forEach(async (tile) => {
      const metrics = buildMetrics({
        attributionModel: tile.attributionModel ?? filters.attributionModel,
        attributionWindow: tile.attributionWindow ?? filters.attributionWindow,
        accountingMode: tile.accountingMode ?? filters.accountingMode,
      });

      const metric = metrics.find((e) => e.id === tile.metricId);

      if (!metric) {
        return;
      }

      if (!hasFeatureFlag("enableDashboardV2") && metric.isDashboardV2) {
        return;
      }

      const dateRange = parseDateRangeArgument(
        filters.dateRange,
        todayLocalAsISODate(),
      );

      const compareDateRange = tile.compareDateRange
        ? deriveComparisonDateRange(
            dateRange,
            tile.compareDateRange,
            user.timezone,
            filters.timeGranularity,
          )
        : null;

      const filterQuery = tile.filterQuery || filters.filterQuery;
      const advancedSearch = tile.advancedSearch ?? filters.advancedSearch;
      const level =
        filters.level ?? deriveLevelFromSearch(filterQuery, advancedSearch);

      const variables: GetMetricsExplorerReportVariables = {
        attributionModel: tile.attributionModel ?? filters.attributionModel,
        accountingMode: tile.accountingMode ?? filters.accountingMode,
        attributionWindow: tile.attributionWindow ?? filters.attributionWindow,
        timeGranularity: filters.timeGranularity,
        level,
        dateRange: compareDateRange ?? dateRange,
        advancedSearch: buildAdvancedSearchJson({
          tileFilterQuery: tile.filterQuery,
          tileAdvancedSearch: tile.advancedSearch,
          filtersFilterQuery: filters.filterQuery,
          filtersAdvancedSearch: filters.advancedSearch,
        }),
        breakdownFilters: tile.filters,
        metricIds: [metric.id],
        dimensionIds: ["date"],
        sorting: [{ dimensionId: "date", order: "asc" }],
      };

      const { data } = await loadMetricData(variables);

      const datasetDates = generateDateLabels(
        compareDateRange ?? dateRange,
        user.timezone,
        filters.timeGranularity,
      );

      const rows: SalesMetricRow[] = [];

      for (const date of datasetDates) {
        const dateString =
          filters.timeGranularity === "hourly"
            ? moment(date).tz("UTC").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
            : moment(date).format("YYYY-MM-DD");
        const row = data.me.metricsExplorerReport.rows.find(
          (e) => e.date === dateString,
        );

        rows.push(row ?? { date: dateString, metrics: {} });
      }

      setMetricsData((prev) =>
        prev.map((e) =>
          e.tileId === tile.id
            ? {
                ...e,
                status: "done",
                data: {
                  rows: rows,
                  summary: data.me.metricsExplorerReport.summary,
                },
              }
            : e,
        ),
      );

      addQueryMetadata({
        context: "Metrics Explorer",
        topic: `Metric Tile: ${tile.name || tile.groupName || metric.name}`,
        metadata: data.me.metricsExplorerReport.queryMetadata,
      });
    });

    prevTiles.current = tiles;
  }, [
    tiles,
    filters.attributionModel,
    filters.attributionWindow,
    filters.accountingMode,
    filters.timeGranularity,
    filters.dateRange,
    filters.filterQuery,
    filters.advancedSearch,
    filters.level,
    user.timezone,
    loadMetricData,
    buildMetrics,
    addQueryMetadata,
    hasFeatureFlag,
  ]);

  useEffect(() => {
    setBenchmarksData(
      tiles.map((tile) => ({
        tileId: tile.id,
        status: "loading",
        benchmark: null,
      })),
    );

    tiles.forEach(async (tile) => {
      const metrics = buildMetrics({
        attributionModel: tile.attributionModel ?? filters.attributionModel,
        attributionWindow: tile.attributionWindow ?? filters.attributionWindow,
        accountingMode: tile.accountingMode ?? filters.accountingMode,
      });

      const metric = metrics.find((e) => e.id === tile.metricId);

      if (!metric) {
        return;
      }

      const filterQuery = tile.filterQuery || filters.filterQuery;
      const advancedSearch = tile.advancedSearch ?? filters.advancedSearch;
      const level = deriveLevelFromSearch(filterQuery, advancedSearch);

      const benchmark = await loadAdoptedBenchmarks({
        attributionModel: tile.attributionModel ?? filters.attributionModel,
        accountingMode: tile.accountingMode ?? filters.accountingMode,
        attributionWindow: tile.attributionWindow ?? filters.attributionWindow,
        level,
        breakdowns: [],
        filters: tile.filters,
        metric,
      });

      setBenchmarksData((prev) =>
        prev.map((e) =>
          e.tileId === tile.id
            ? {
                tileId: tile.id,
                status: "done",
                benchmark: benchmark?.["topLevel"] ?? null,
              }
            : e,
        ),
      );
    });
  }, [tiles, filters, buildMetrics, loadAdoptedBenchmarks]);

  useEffect(() => {
    if (!hasFeatureFlag("enableNorthbeamIndex")) {
      return;
    }

    const indexTiles = tiles.filter((e) => e.showNBX);

    setIndexData(
      indexTiles.map((tile) => ({
        tileId: tile.id,
        status: "loading",
        type: "nbx",
        data: null,
      })),
    );

    indexTiles
      .filter((e) => e.showNBX)
      .forEach(async (tile) => {
        const metrics = buildMetrics({
          attributionModel: tile.attributionModel ?? filters.attributionModel,
          attributionWindow:
            tile.attributionWindow ?? filters.attributionWindow,
          accountingMode: tile.accountingMode ?? filters.accountingMode,
        });

        const metric = metrics.find((e) => e.id === tile.metricId);

        if (!metric) {
          return;
        }

        const dateRange = parseDateRangeArgument(
          filters.dateRange,
          todayLocalAsISODate(),
        );

        const compareDateRange = tile.compareDateRange
          ? deriveComparisonDateRange(
              dateRange,
              tile.compareDateRange,
              user.timezone,
              filters.timeGranularity,
            )
          : null;

        const { data } = await loadNorthbeamIndexData({
          attributionModel: tile.attributionModel ?? filters.attributionModel,
          accountingMode: tile.accountingMode ?? filters.accountingMode,
          attributionWindow:
            tile.attributionWindow ?? filters.attributionWindow,
          dateRange: compareDateRange ?? dateRange,
          indexType: "nbx",
          metric: metric.id,
        });

        setIndexData((prev) =>
          prev.map((e) =>
            e.tileId === tile.id
              ? {
                  tileId: tile.id,
                  status: "done",
                  type: "nbx",
                  data: data.me.northbeamIndex.rows,
                }
              : e,
          ),
        );
      });
  }, [tiles, filters, buildMetrics, loadNorthbeamIndexData]);

  const { data: topSpendPlatformsData, loading: isTopSpendPlatformsLoading } =
    useNorthbeamQuery<GetTopSpendPlatforms>(GET_TOP_SPEND_PLATFORMS);

  return (
    <MetricsExplorerContext.Provider
      value={{
        filters: { ...filters, dateRangeInterval },
        tiles,
        chartState,
        isMetadataLoading: isTopSpendPlatformsLoading,
        metricsData,
        benchmarksData,
        indexData,
        topSpendPlatforms: topSpendPlatformsData?.me.topSpendPlatforms ?? [],
        pdfExportAreaRef,
        setPdfExportAreaRef,
        setTiles,
        setAlignAxesScale,
        buildMetrics,
      }}
    >
      {children}
    </MetricsExplorerContext.Provider>
  );
};
